import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, isAdmin } from "../lib/api";
import { Target, Trophy, X, Zap, ClipboardEdit, Save, Edit3, ArrowRight } from "lucide-react";
import LiveLegTracker from "../components/LiveLegTracker";

const emptyLeg = () => ({ winner_id: "", finish_score: "", ausgebullt: false });

const emptyMatchStats = () => ({
  match_avg_p1: "", match_avg_p2: "",
  match_s180_p1: 0, match_s180_p2: 0,
});

/**
 * EnterMatch v3:
 *  - Legs: nur Sieger + Finish/Ausbullen
 *  - Match-Level: Average / 180er einmal am Ende; High Finish wird aus den Legs berechnet
 *  - Bearbeitung nachträglich (Pre-fill wenn Match bereits gespielt)
 */
export default function EnterMatch() {
  const [params] = useSearchParams();
  const preId = params.get("match");
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [matchId, setMatchId] = useState(preId || "");
  const [legs, setLegs] = useState([emptyLeg(), emptyLeg()]);
  const [matchStats, setMatchStats] = useState(emptyMatchStats());
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("quick");
  const [liveLegIdx, setLiveLegIdx] = useState(0);
  const admin = isAdmin();

  useEffect(() => {
    (async () => {
      const [ss, ps] = await Promise.all([api.listSeasons(), api.listPlayers()]);
      setSeasons(ss); setPlayers(ps);
      const active = ss.find((s) => s.active) || ss[0];
      if (active) setSeasonId(active.id);
    })();
  }, []);

  useEffect(() => {
    if (!seasonId) return;
    api.listMatches({ season_id: seasonId }).then((ms) => {
      setMatches(ms);
      if (preId && ms.find((m) => m.id === preId)) setMatchId(preId);
    });
  }, [seasonId, preId]);

  const pmap = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const match = matches.find((m) => m.id === matchId);
  const p1 = match ? pmap[match.player1_id] : null;
  const p2 = match ? pmap[match.player2_id] : null;
  const isEdit = !!match?.played;

  // Pre-fill from played match
  useEffect(() => {
    if (!match) {
      setLegs([emptyLeg(), emptyLeg()]);
      setMatchStats(emptyMatchStats());
      return;
    }
    if (match.played && match.legs?.length) {
      setLegs(match.legs.map((l) => ({
        winner_id: l.winner_id,
        finish_score: l.finish_score ?? "",
        ausgebullt: !!l.ausgebullt,
      })));
      setMatchStats({
        match_avg_p1: match.match_avg_p1 ?? "",
        match_avg_p2: match.match_avg_p2 ?? "",
        match_s180_p1: match.match_s180_p1 ?? 0,
        match_s180_p2: match.match_s180_p2 ?? 0,
      });
    } else {
      setLegs([emptyLeg(), emptyLeg()]);
      setMatchStats(emptyMatchStats());
    }
    setMode("quick");
    setLiveLegIdx(0);
  }, [matchId]);

  const setLeg = (idx, patch) => setLegs((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const legsP1 = legs.filter((l) => l.winner_id === match?.player1_id).length;
  const legsP2 = legs.filter((l) => l.winner_id === match?.player2_id).length;
  const needThird = legsP1 === 1 && legsP2 === 1 && legs.length === 2;

  useEffect(() => {
    if (needThird && legs.length === 2) setLegs([...legs, emptyLeg()]);
  }, [needThird]);

  const submit = async (continueToNext = false) => {
    if (!match) { toast.error("Kein Match ausgewählt"); return; }
    const filled = legs.filter((l) => l.winner_id);
    if (filled.length < 2) { toast.error("Mindestens 2 Legs erforderlich"); return; }
    const p1Wins = filled.filter((l) => l.winner_id === match.player1_id).length;
    const p2Wins = filled.filter((l) => l.winner_id === match.player2_id).length;
    if (p1Wins < 2 && p2Wins < 2) { toast.error("Ein Spieler muss 2 Legs gewinnen"); return; }
    const legsPayload = filled.map((l, idx) => ({
      leg_number: idx + 1,
      winner_id: l.winner_id,
      finish_score: l.finish_score !== "" ? parseInt(l.finish_score) : null,
      ausgebullt: !!l.ausgebullt,
    }));
    const payload = {
      legs: legsPayload,
      match_avg_p1: matchStats.match_avg_p1 !== "" ? parseFloat(matchStats.match_avg_p1) : null,
      match_avg_p2: matchStats.match_avg_p2 !== "" ? parseFloat(matchStats.match_avg_p2) : null,
      match_s180_p1: parseInt(matchStats.match_s180_p1) || 0,
      match_s180_p2: parseInt(matchStats.match_s180_p2) || 0,
    };
    try {
      setSubmitting(true);
      const res = await axiosSubmit(match.id, payload);
      const winner = res.winner_id === match.player1_id ? p1?.name : p2?.name;
      toast.success(isEdit ? `Ergebnis aktualisiert! Sieger: ${winner}` : `Ergebnis gespeichert! Sieger: ${winner}`);
      setMatches((previous) => previous.map((item) => item.id === match.id ? {
        ...item, played: true, winner_id: res.winner_id, legs_p1: res.legs_p1, legs_p2: res.legs_p2,
      } : item));
      if (continueToNext && !isEdit) {
        const nextMatch = matches.find((item) => item.matchday_id === match.matchday_id && !item.played && item.id !== match.id);
        if (nextMatch) {
          setMatchId(nextMatch.id);
          toast.success(`Weiter mit ${pmap[nextMatch.player1_id]?.name || "?"} gegen ${pmap[nextMatch.player2_id]?.name || "?"}`);
          return;
        }
        toast.info("Kein weiteres offenes Spiel an diesem Spieltag");
      }
      navigate("/spielplan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  };

  const openMatches = matches.filter((m) => !m.played);
  const playedMatches = matches.filter((m) => m.played);

  if (!admin) {
    return (
      <div className="dc-border p-10 text-center space-y-4" data-testid="enter-match-admin-only">
        <div className="font-display text-3xl">ADMIN-BEREICH</div>
        <p style={{ color: "var(--dc-muted)" }}>Ergebnisse können nur nach der Admin-Anmeldung erfasst oder geändert werden.</p>
        <button className="dc-btn" onClick={() => navigate("/admin")}>Zum Admin-Login</button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enter-match-page">
      <div>
        <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>{isEdit ? "Ergebnis bearbeiten" : "Ergebnis erfassen"}</div>
        <h1 className="font-display text-5xl">{isEdit ? "Match Editor" : "Best of 3"}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "var(--dc-muted)" }}>Saison</label>
          <select className="dc-input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="enter-season-select">
            {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "var(--dc-muted)" }}>Partie {playedMatches.length > 0 && "(inkl. gespielte zum Bearbeiten)"}</label>
          <select className="dc-input" value={matchId} onChange={(e) => setMatchId(e.target.value)} data-testid="enter-match-select">
            <option value="">-- wählen --</option>
            {openMatches.length > 0 && <optgroup label="Offen">
              {openMatches.map((m) => (
                <option key={m.id} value={m.id}>
                  ST{m.matchday_number || "?"}: {pmap[m.player1_id]?.name || "?"} vs {pmap[m.player2_id]?.name || "?"}{!m.is_ranked ? " · Gast" : ""}
                </option>
              ))}
            </optgroup>}
            {playedMatches.length > 0 && <optgroup label="Gespielt (bearbeiten)">
              {playedMatches.map((m) => (
                <option key={m.id} value={m.id}>
                  ST{m.matchday_number || "?"}: {pmap[m.player1_id]?.name || "?"} {m.legs_p1}:{m.legs_p2} {pmap[m.player2_id]?.name || "?"}{!m.is_ranked ? " · Gast" : ""}
                </option>
              ))}
            </optgroup>}
          </select>
        </div>
      </div>

      {match && p1 && p2 && (
        <>
          <div className="flex gap-2 border-b dc-border">
            <button className={`dc-tab flex items-center gap-2 ${mode === "quick" ? "active" : ""}`} onClick={() => setMode("quick")} data-testid="mode-quick">
              <ClipboardEdit size={14} /> Schnelleingabe
            </button>
            <button className={`dc-tab flex items-center gap-2 ${mode === "live" ? "active" : ""}`} onClick={() => { setMode("live"); setLiveLegIdx(0); setLegs([emptyLeg(), emptyLeg()]); setMatchStats(emptyMatchStats()); }} data-testid="mode-live" disabled={isEdit}>
              <Zap size={14} /> Live-Tracker {isEdit && "(neue Erfassung)"}
            </button>
          </div>

          {isEdit && (
            <div className="dc-border p-4 flex items-center gap-2 text-sm" style={{ background: "rgba(245,209,59,0.08)", borderColor: "var(--dc-chalk)" }} data-testid="edit-notice">
              <Edit3 size={16} style={{ color: "var(--dc-chalk)" }} />
              <span>Du bearbeitest ein bereits gespieltes Match. Änderungen überschreiben die vorherigen Werte.</span>
            </div>
          )}

          <MatchHeader p1={p1} p2={p2} legsP1={legsP1} legsP2={legsP2} />

          {mode === "quick" && (
            <>
              {legs.map((leg, idx) => (
                <LegForm key={`leg-${idx + 1}`} idx={idx} leg={leg} p1={p1} p2={p2}
                  onChange={(patch) => setLeg(idx, patch)}
                  onRemove={idx === 2 ? () => setLegs(legs.slice(0, 2)) : null} />
              ))}
              <MatchStatsForm p1={p1} p2={p2} stats={matchStats} onChange={setMatchStats} />
            </>
          )}

          {mode === "live" && (() => {
            const decided = legsP1 >= 2 || legsP2 >= 2;
            return (
              <>
                {legs.slice(0, liveLegIdx).map((leg, i) => (
                  <div key={`live-summary-${i + 1}`} className="dc-border p-4 flex justify-between items-center" data-testid={`live-leg-summary-${i + 1}`}>
                    <div className="font-display text-lg">Leg {i + 1}</div>
                    <div className="font-mono-data text-sm">
                      Sieger: <span className="text-white">{leg.winner_id === p1.id ? p1.name : p2.name}</span>
                      {leg.finish_score && <> · Finish <span className="text-white">{leg.finish_score}</span></>}
                      {leg.ausgebullt && <span style={{ color: "var(--dc-chalk)" }}> · Ausgebullt</span>}
                    </div>
                  </div>
                ))}
                {!decided && liveLegIdx < 3 && (
                  <LiveLegTracker
                    key={liveLegIdx}
                    p1={p1} p2={p2}
                    legNumber={liveLegIdx + 1}
                    onFinish={(legData) => {
                      setLegs((prev) => {
                        const copy = [...prev];
                        while (copy.length <= liveLegIdx) copy.push(emptyLeg());
                        copy[liveLegIdx] = {
                          winner_id: legData.winner_id,
                          finish_score: legData.finish_score ?? "",
                          ausgebullt: !!legData.ausgebullt,
                        };
                        return copy.slice(0, Math.max(2, liveLegIdx + 1));
                      });
                      // Live-Tracker aggregates match-level stats over legs
                      setMatchStats((s) => {
                        const p1Avgs = [s.match_avg_p1, legData.avg_p1].filter((v) => v !== "" && v !== null && v !== undefined && v !== 0);
                        const p2Avgs = [s.match_avg_p2, legData.avg_p2].filter((v) => v !== "" && v !== null && v !== undefined && v !== 0);
                        const avgP1 = p1Avgs.length ? +(p1Avgs.reduce((a, b) => Number(a) + Number(b), 0) / p1Avgs.length).toFixed(2) : "";
                        const avgP2 = p2Avgs.length ? +(p2Avgs.reduce((a, b) => Number(a) + Number(b), 0) / p2Avgs.length).toFixed(2) : "";
                        return {
                          match_avg_p1: avgP1,
                          match_avg_p2: avgP2,
                          match_s180_p1: (parseInt(s.match_s180_p1) || 0) + (legData.s180_p1 || 0),
                          match_s180_p2: (parseInt(s.match_s180_p2) || 0) + (legData.s180_p2 || 0),
                        };
                      });
                      setLiveLegIdx((i) => i + 1);
                    }}
                    onCancel={() => setMode("quick")}
                  />
                )}
                {decided && (
                  <>
                    <div className="dc-border p-6 text-center" style={{ background: "rgba(35,148,88,0.1)" }}>
                      <div className="font-display text-3xl" style={{ color: "var(--dc-secondary)" }}>Match entschieden!</div>
                      <div className="text-sm mt-2" style={{ color: "var(--dc-muted)" }}>Kontrolliere die Match-Statistik und speichere.</div>
                    </div>
                    <MatchStatsForm p1={p1} p2={p2} stats={matchStats} onChange={setMatchStats} />
                  </>
                )}
              </>
            );
          })()}

          <div className="flex flex-wrap justify-between gap-2">
            <button className="dc-btn dc-btn-ghost" onClick={() => navigate(-1)} data-testid="enter-cancel-button">Abbrechen</button>
            <div className="flex flex-wrap gap-2">
              <button className="dc-btn dc-btn-secondary" onClick={() => submit(false)} disabled={submitting} data-testid="enter-submit-button">
                {isEdit ? <Save size={16} /> : <Trophy size={16} />}
                {submitting ? "Speichern..." : (isEdit ? "Änderungen speichern" : "Nur speichern")}
              </button>
              {!isEdit && <button className="dc-btn" onClick={() => submit(true)} disabled={submitting} data-testid="enter-submit-next-button">
                <Save size={16} /> Speichern & nächstes Spiel <ArrowRight size={14} />
              </button>}
            </div>
          </div>
        </>
      )}
      {!match && (
        <div className="dc-border p-10 text-center" style={{ color: "var(--dc-muted)" }}>
          Bitte oben eine Partie auswählen.
        </div>
      )}
    </div>
  );
}

// helper to keep axios out of dependency chain
async function axiosSubmit(id, payload) {
  return await api.submitResult(id, payload.legs, payload);
}

function MatchHeader({ p1, p2, legsP1, legsP2 }) {
  return (
    <div className="dc-border p-6" style={{ background: "var(--dc-surface)" }}>
      <div className="grid grid-cols-3 items-center gap-4">
        <div className="text-center md:text-right">
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>Spieler 1</div>
          <div className="font-display text-4xl">{p1.name}</div>
        </div>
        <div className="text-center font-display text-6xl font-mono-data flex justify-center items-center gap-3">
          <span style={{ color: legsP1 >= 2 ? "var(--dc-secondary)" : "var(--dc-text)" }} data-testid="score-p1">{legsP1}</span>
          <span style={{ color: "var(--dc-muted)" }}>:</span>
          <span style={{ color: legsP2 >= 2 ? "var(--dc-secondary)" : "var(--dc-text)" }} data-testid="score-p2">{legsP2}</span>
        </div>
        <div className="text-center md:text-left">
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>Spieler 2</div>
          <div className="font-display text-4xl">{p2.name}</div>
        </div>
      </div>
    </div>
  );
}

function LegForm({ idx, leg, p1, p2, onChange, onRemove }) {
  const winnerIsP1 = leg.winner_id === p1.id;
  const winnerIsP2 = leg.winner_id === p2.id;
  return (
    <div className="dc-border p-6 space-y-4" data-testid={`leg-form-${idx + 1}`}>
      <div className="flex items-center justify-between">
        <div className="font-display text-2xl flex items-center gap-2"><Target size={16} /> Leg {idx + 1}</div>
        {onRemove && (
          <button className="dc-btn dc-btn-ghost text-xs" onClick={onRemove} data-testid={`leg-remove-${idx + 1}`}>
            <X size={14} /> Entfernen
          </button>
        )}
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--dc-muted)" }}>Sieger</div>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" className="dc-btn"
            style={{ background: winnerIsP1 ? "var(--dc-secondary)" : "transparent",
                     borderColor: winnerIsP1 ? "var(--dc-secondary)" : "var(--dc-border)",
                     color: winnerIsP1 ? "white" : "var(--dc-text)" }}
            onClick={() => onChange({ winner_id: p1.id })}
            data-testid={`leg-${idx + 1}-winner-p1`}>{p1.name}</button>
          <button type="button" className="dc-btn"
            style={{ background: winnerIsP2 ? "var(--dc-secondary)" : "transparent",
                     borderColor: winnerIsP2 ? "var(--dc-secondary)" : "var(--dc-border)",
                     color: winnerIsP2 ? "white" : "var(--dc-text)" }}
            onClick={() => onChange({ winner_id: p2.id })}
            data-testid={`leg-${idx + 1}-winner-p2`}>{p2.name}</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Finish (Score)</label>
          <input type="number" className="dc-input font-mono-data" placeholder="z.B. 40, 100, 170" value={leg.finish_score}
            onChange={(e) => onChange({ finish_score: e.target.value })} disabled={leg.ausgebullt} data-testid={`leg-${idx + 1}-finish`} />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" className="w-4 h-4 accent-[color:var(--dc-primary)]" checked={leg.ausgebullt}
              onChange={(e) => onChange({ ausgebullt: e.target.checked, finish_score: e.target.checked ? "" : leg.finish_score })}
              data-testid={`leg-${idx + 1}-ausgebullt`} />
            <span className="text-sm" style={{ color: "var(--dc-chalk)" }}>Ausgebullt (statt Double Out)</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function MatchStatsForm({ p1, p2, stats, onChange }) {
  const upd = (patch) => onChange({ ...stats, ...patch });
  return (
    <div className="dc-border p-6 space-y-4" data-testid="match-stats-form">
      <div className="font-display text-2xl">Match-Statistik (nach allen Legs)</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PlayerStatsRow label={p1.name} suffix="p1" stats={stats} onUpdate={upd} />
        <PlayerStatsRow label={p2.name} suffix="p2" stats={stats} onUpdate={upd} />
      </div>
    </div>
  );
}

function PlayerStatsRow({ label, suffix, stats, onUpdate }) {
  return (
    <div className="space-y-3">
      <div className="font-display text-xl">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Match-Average</label>
          <input type="number" step="0.01" className="dc-input font-mono-data" value={stats[`match_avg_${suffix}`]}
            onChange={(e) => onUpdate({ [`match_avg_${suffix}`]: e.target.value })}
            data-testid={`match-avg-${suffix}`} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>180er (gesamt)</label>
          <input type="number" min="0" className="dc-input font-mono-data" value={stats[`match_s180_${suffix}`]}
            onChange={(e) => onUpdate({ [`match_s180_${suffix}`]: e.target.value })}
            data-testid={`match-s180-${suffix}`} />
        </div>
      </div>
    </div>
  );
}
